import React, { useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, BackToListLink, Button } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { getBlogPostDetail, deleteBlogPost } from '@/services/blogPostService';
import { formatDateShort } from '@/utils/dateUtils';
import { useApiLanguage } from '@/hooks/useApiLanguage';

/**
 * 블로그 글 상세 페이지.
 * /blog/:id
 */
export default function BlogDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('menu');
  const queryClient = useQueryClient();
  const isAdmin = location.pathname.startsWith('/admin/blog');
  const listRoute = isAdmin ? ROUTES.ADMIN.BLOG : ROUTES.COMMON.BLOG;
  const numId = Number(id);
  const lang = useApiLanguage();

  const { data: post, isLoading } = useQuery({
    queryKey: ['blogPostDetail', numId, lang, isAdmin],
    queryFn: () => getBlogPostDetail(numId, lang, { skipAuth: !isAdmin }),
    enabled: !!numId && !isNaN(numId),
    staleTime: 0,
  });

  const [deleting, setDeleting] = useState(false);

  const handleDeletePost = async () => {
    if (!window.confirm(t('blogPage.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await deleteBlogPost(numId);
      queryClient.invalidateQueries({ queryKey: ['blogPostList'] });
      navigate(listRoute);
    } catch {
      alert(t('blogPage.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 py-8">
        <p className="text-gray-500">{t('blogPage.loading')}</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 py-8">
        <BackToListLink to={listRoute} className="mb-6">
          {t('blogPage.backToList')}
        </BackToListLink>
        <p className="text-gray-500">{t('blogPage.notFound')}</p>
      </div>
    );
  }

  const thumbUrl = typeof post.thumbnailImageUrl === 'string' ? post.thumbnailImageUrl : '';

  return (
    <div className="max-w-[1280px] mx-auto px-4 py-8">
      <BackToListLink to={listRoute} className="mb-4">
        {t('blogPage.backToList')}
      </BackToListLink>

      <PageHeader
        title={post.title}
        description={post.publishedAt ? formatDateShort(post.publishedAt, i18n.language) : undefined}
        rightContent={
          isAdmin ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="md"
                onClick={handleDeletePost}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 size={16} className="mr-2" />
                {deleting ? t('blogPage.deleting') : t('blogPage.delete')}
              </Button>
              <Link to={ROUTES.ADMIN.BLOG_EDIT(String(post.id))}>
                <Button variant="outline" size="md">
                  {t('blogPage.editButton')}
                </Button>
              </Link>
            </div>
          ) : null
        }
      />

      {thumbUrl && (
        <div className="mt-4 w-full max-w-2xl">
          <img src={thumbUrl} alt={t('blogPage.thumbnailAlt')} className="rounded-lg border border-gray-200 object-cover w-full max-h-64" />
        </div>
      )}

      <div className="mt-6">
        {post.content && (
          <div
            className="blog-detail-body rounded-lg border border-gray-200 bg-white p-6 text-gray-700 prose prose-gray max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content.trim() }}
          />
        )}
      </div>
    </div>
  );
}
